import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { IconCarFilled } from 'wg-fe-ui';
import VehicleLookupInputContainer from './VehicleLookupInputContainer';
import VehicleLookupOrderTable from './VehicleLookupOrderTable';
import cogoToast from 'cogo-toast';
import { retrieveAutomobileByCarIdREST } from '../legacy/services/apiRouterService';
import { Brand, Model, VehicleChoices } from '../../../../types/SMT';
import { useDebouncedCallback } from 'use-debounce/lib';
import LoadingOverlay from './LoadingOverlay';
import { CarResponse } from '../../../../types/VehicleResponse';
import useBreakPoint from '../hooks/useBreakPoint';
import { mq } from '../utils/styledComponentsUtils';
import { getBrokerData } from '../legacy/services/brokerDataService';
import { useBreakpointValue } from '@wegroup/design-system';
import useTracking from '../hooks/tracking/useTracking';

interface Props {
  onChange: (p: CarResponse | undefined) => void;
  vehicleChoices?: VehicleChoices;
  isLoading?: boolean;
  setIsLoading: (p: boolean) => void;
  brandFilter?: string[];
  isCampaign?: boolean;
  categories?: Array<'PASSENGER_CAR' | 'LIGHT_COMMERCIAL_VEHICLE'>;
  error?: boolean;
  hasError?: {
    brand: boolean;
    model: boolean;
    fuel: boolean;
    kw: boolean;
  };
  setHasError?: React.Dispatch<
    React.SetStateAction<{
      brand: boolean;
      model: boolean;
      fuel: boolean;
      kw: boolean;
    }>
  >;
}

const VehicleLookupComponentV2: React.FC<React.PropsWithChildren<Props>> = ({
  vehicleChoices: vehicleChoicesProps,
  onChange,
  isLoading,
  setIsLoading,
  brandFilter,
  isCampaign,
  error,
  setHasError,
  hasError,
  categories,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const [brand, setBrand] = useState<Brand | undefined>(
    vehicleChoicesProps?.brand?.value,
  );
  const [model, setModel] = useState<Model | undefined>(
    vehicleChoicesProps?.model?.value,
  );
  const [fuel, setFuel] = useState<string | undefined>(
    vehicleChoicesProps?.fuel?.value,
  );
  const [kw, setKw] = useState<number | undefined>(
    vehicleChoicesProps?.kw?.value,
  );
  const [selected, setSelected] = useState<string | undefined>(
    vehicleChoicesProps?.selected,
  );
  const [vehicleChoices, setVehicleChoices] = useState<
    VehicleChoices | undefined
  >(vehicleChoicesProps);

  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);

  const { trackEvent } = useTracking();
  const { width } = useBreakPoint();
  const isMobile = useBreakpointValue({ base: true, tablet: false });

  // Both states are used for mobile UI altering
  const [mobile, setMobile] = useState(!!isMobile);

  useEffect(() => {
    if (error && setHasError) {
      setHasError({
        brand: !brand && error,
        model: !model && error,
        fuel: !fuel && error,
        kw: !kw && error,
      });
    }
  }, [error]);

  useEffect(() => {
    if (isMobile && !mobile) {
      setMobile(true);
    } else if (!isMobile && mobile) {
      setMobile(false);
    }
  }, [width, isMobile]);

  useEffect(() => {
    if (!brand) {
      onChange(undefined);
    }
  }, [brand]);

  const handleSelectedCar = useCallback(
    async (_brand: string, modelId: string, versionId: string) => {
      trackEvent('Lookup In Toolkit Car Search Manual');
      const { broker } = isCampaign
        ? { broker: { plan: 'BASIC' } }
        : await getBrokerData();
      setSelected(versionId);
      setIsLoading(true);
      const [resp, status] = await retrieveAutomobileByCarIdREST(
        versionId,
        broker.plan === 'BASIC' ? 'freemium' : 'premium',
      );

      if (status !== 200 || resp?.code === 404) {
        cogoToast.error(
          resp?.msg || t('There was an error while retrieving the car data'),
        );
        onChange(undefined);
      } else {
        resp.data.vehicleChoices = { ...vehicleChoices, selected: versionId };
        onChange(resp);
      }

      setIsLoading(false);
      setIsDataLoading(false);
    },
    [vehicleChoices],
  );

  const [debounceHandleSelectedCar] = useDebouncedCallback(
    handleSelectedCar,
    500,
  );

  const handleSelect = useCallback(
    (_brand: string, _model: string, _version: string) => {
      setIsDataLoading(true);
      debounceHandleSelectedCar(_brand, _model, _version);
    },
    [],
  );

  return (
    <MainContainer
      data-test-id="Common_VehicleLookupComponentV2"
      {...otherProps}
    >
      {(isDataLoading || isLoading) && (
        <LoadingOverlay>{t('Retrieving vehicle data')}</LoadingOverlay>
      )}
      <Container data-test-id="tk_vehicle_lookup_input_container">
        <BrandLogoContainer>
          {brand?.logo ? (
            <BrandLogo
              src={brand?.logo}
              data-test-id="Common_VehicleLookupComponentV2_BrandLogo"
            />
          ) : (
            <LogoPlaceholder>
              <IconCarFilled size={80} color="#c1c1c1" />
            </LogoPlaceholder>
          )}
        </BrandLogoContainer>
        <VehicleLookupInputContainer
          setBrand={setBrand}
          setModel={setModel}
          setFuel={setFuel}
          setKw={setKw}
          vehicleChoices={vehicleChoices}
          setVehicleChoices={setVehicleChoices}
          brandFilter={brandFilter}
          selectedValues={{ brand, model, fuelType: fuel, kw }}
          hasError={hasError}
          setHasError={setHasError}
          categories={categories}
        />
      </Container>
      <VehicleLookupOrderTable
        selectedValues={{ brand, model, fuelType: fuel, kw }}
        handleSelectedCar={handleSelect}
        selected={selected}
        mobile={mobile}
        dataTestId="vehicle_lookup_order_table"
        categories={categories}
      />
    </MainContainer>
  );
};

const MainContainer = styled.div`
  position: relative;
`;

const Container = styled.div`
  ${mq.tablet} {
    display: grid;
    grid-template-columns: 17rem 1fr;
    grid-column-gap: 3.2rem;
  }
`;

const BrandLogo = styled.img`
  display: block;
  max-height: 100%;
  max-width: 100%;
  margin: auto;
`;

const LogoPlaceholder = styled.p`
  text-transform: uppercase;
  color: #8990a3;
`;

const BrandLogoContainer = styled.div`
  display: none;

  ${mq.tablet} {
    width: 17rem;
    height: 17rem;
    padding: 0.8rem;
    border: 1px solid #f0f1f3;
    border-radius: 0.5rem;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
      0 0 1px rgba(0, 0, 0, 0.04); /* Using px for scaling purpose **/
    background-color: white;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

export default VehicleLookupComponentV2;
